import { useEffect } from 'react';
import { SITE, IMAGES } from '../data/site';

const upsert = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

export const SEO = ({ title, description, path = '/', jsonLd = [], image, noindex = false }) => {
  const fullTitle = title
    ? (title.includes(SITE.name) ? title : `${title} | ${SITE.name}`)
    : `${SITE.name} | Custom Outdoor Kitchens & Sunrooms in Omaha & Lincoln, Nebraska`;
  const canonical = `${SITE.baseUrl}${path}`;
  useEffect(() => {
    document.title = fullTitle;
    upsert('name', 'description', description || SITE.tagline);
    upsert('name', 'robots', noindex ? 'noindex, follow' : 'index, follow');
    upsert('property', 'og:title', fullTitle);
    upsert('property', 'og:description', description || SITE.tagline);
    upsert('property', 'og:url', canonical);
    upsert('property', 'og:type', 'website');
    upsert('property', 'og:site_name', SITE.name);
    upsert('property', 'og:image', image || IMAGES.hero.src);
    upsert('name', 'twitter:card', 'summary_large_image');
    upsert('name', 'twitter:title', fullTitle);
    upsert('name', 'twitter:description', description || SITE.tagline);
    upsert('name', 'twitter:image', image || IMAGES.hero.src);
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);
    document.querySelectorAll('script[data-seo-jsonld]').forEach((n) => n.remove());
    jsonLd.forEach((obj) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo-jsonld', 'true');
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });
  }, [fullTitle, description, canonical, image, jsonLd]);
  return null;
};

export const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'HomeAndConstructionBusiness',
  name: SITE.name,
  alternateName: SITE.legalName,
  url: SITE.baseUrl,
  telephone: SITE.phone,
  email: SITE.email,
  areaServed: ['Omaha NE', 'Lincoln NE', 'Millard NE', 'Papillion NE', 'La Vista NE', 'Gretna NE', 'Elkhorn NE', 'Bennington NE', 'Wahoo NE', 'Ashland NE'],
  description: 'Custom outdoor living design and construction — outdoor kitchens, sunrooms, decks, pergolas, patios, and water & fire features — serving the Omaha and Lincoln, Nebraska metro areas for over 22 years.',
};

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE.name,
  legalName: SITE.legalName,
  url: SITE.baseUrl,
  logo: `${SITE.baseUrl}/images/logo.png`,
  telephone: SITE.phone,
  email: SITE.email,
};

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE.name,
  url: SITE.baseUrl,
  publisher: { '@type': 'Organization', name: SITE.name },
};

export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${SITE.baseUrl}${it.path}`,
  })),
});

export const faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});
